import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * ErrorBoundary component to catch JavaScript errors in lazy-loaded components
 * and display a fallback UI instead of crashing the whole app
 * Uses semantic styling and configuration support
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // You can log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  getErrorMessage(error) {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    switch (error?.code) {
      // Circle of Trust specific errors
      case 'CIRCLE_NOT_FOUND':
        return 'The requested circle could not be found.';
      case 'CIRCLE_QUOTA_EXCEEDED':
        return 'You have reached the maximum number of circles allowed.';
      case 'CIRCLE_MEMBER_QUOTA_EXCEEDED':
        return 'This circle has reached the maximum number of members allowed.';
      case 'CIRCLE_INVITATION_EXPIRED':
        return 'The invitation has expired. Please request a new one.';
      case 'CIRCLE_INVITATION_INVALID':
        return 'The invitation is invalid or has already been used.';
      case 'CIRCLE_MEMBER_NOT_FOUND':
        return 'The specified member could not be found in this circle.';
      case 'CIRCLE_PERMISSION_DENIED':
        return 'You do not have permission to perform this action.';
      default:
        return error?.message || 'An unexpected error occurred.';
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorBoundaryFallback error={this.state.error} showErrorDetails={this.props.showErrorDetails} />;
    }

    return this.props.children; 
  }
}

// Separate component to use hooks
const ErrorBoundaryFallback = ({ error, showErrorDetails }) => {
  const colors = useColors();
  const semantic = colors.semantic;
  const { replace } = useTextReplacement();

  const getErrorMessage = (err) => {
    if (err?.response?.data?.message) {
      return err.response.data.message;
    }
    switch (err?.code) {
      case 'CIRCLE_NOT_FOUND':
        return 'The requested circle could not be found.';
      case 'CIRCLE_QUOTA_EXCEEDED':
        return 'You have reached the maximum number of circles allowed.';
      case 'CIRCLE_MEMBER_QUOTA_EXCEEDED':
        return 'This circle has reached the maximum number of members allowed.';
      case 'CIRCLE_INVITATION_EXPIRED':
        return 'The invitation has expired. Please request a new one.';
      case 'CIRCLE_INVITATION_INVALID':
        return 'The invitation is invalid or has already been used.';
      case 'CIRCLE_MEMBER_NOT_FOUND':
        return 'The specified member could not be found in this circle.';
      case 'CIRCLE_PERMISSION_DENIED':
        return 'You do not have permission to perform this action.';
      default:
        return err?.message || 'An unexpected error occurred.';
    }
  };

  return (
    <div className={`text-center p-6 ${semantic.highlightPrimaryBg} ${semantic.highlightPrimaryBorder} border rounded-lg mt-4`}>
      <h2 className={`text-xl ${semantic.highlightPrimaryText} mb-3`}>Something went wrong</h2>
      <p className={`${semantic.contentCardText} mb-4`}>
        We're having trouble loading this component. Please try refreshing the page.
      </p>
      <button 
        onClick={() => window.location.reload()}
        className="cta-button-primary px-4 py-2 rounded transition-colors"
      >
        Refresh Page
      </button>
      {showErrorDetails && error && (
        <div className="mt-4 text-left">
          <p className={`text-sm ${semantic.highlightPrimaryText} font-mono ${semantic.contentCardBg} p-3 rounded overflow-auto`}>
            {getErrorMessage(error)}
          </p>
        </div>
      )}
    </div>
  );
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  showErrorDetails: PropTypes.bool
};

ErrorBoundary.defaultProps = {
  showErrorDetails: false
};

ErrorBoundaryFallback.propTypes = {
  error: PropTypes.object,
  showErrorDetails: PropTypes.bool
};

export default ErrorBoundary;

